import { collection, orderBy, query } from "firebase/firestore";
import { useParams } from "react-router-dom";
import { ErrorText, LoadingView, SBCard } from "@/components/ui";
import { db } from "@/firebase/app";
import { useCollection } from "@/hooks/firestoreHooks";
import { parseTrackerDocument, trackerStatus, DEVICE_STATUS_LABELS } from "@/models/gps";
import { useCompetitionDisplay } from "./useCompetitionDisplay";
import "@/features/home/home.css";

// Port of GPSDataView: raw tracker document table for a competition.

export function GpsData() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const { display, members } = useCompetitionDisplay(instanceId);
  const trackersQuery = instanceId
    ? query(collection(db, "leagueCompetitionInstances", instanceId, "gpsData"), orderBy("lastUpdatedAt", "desc"))
    : null;
  const trackers = useCollection(trackersQuery, parseTrackerDocument, `gpsData:${instanceId}`);
  const now = new Date();

  return (
    <div className="list-screen">
      <div className="list-screen-head">
        <div>
          <h1>GPS Data</h1>
          <p className="home-hint">{display?.eventName}</p>
        </div>
      </div>
      <ErrorText>{trackers.error}</ErrorText>
      {trackers.loading && trackers.data.length === 0 ? (
        <LoadingView />
      ) : (
        <SBCard style={{ overflowX: "auto", padding: 0 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                {["GPS ID", "Name", "Device", "Status", "Last update", "Lat", "Lng", "mph", "Alt (ft)", "Battery", "Signal", "Course"].map(
                  (header) => (
                    <th
                      key={header}
                      style={{
                        textAlign: "left",
                        padding: "10px 12px",
                        borderBottom: "1px solid var(--border-subtle)",
                        color: "var(--text-secondary)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {header}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {trackers.data.map((tracker) => {
                const sample = tracker.latestSample;
                const name =
                  tracker.displayName ?? members.find((m) => m.userID === tracker.userID)?.displayName ?? tracker.userID.slice(0, 8);
                return (
                  <tr key={tracker.id}>
                    <td style={cell}>{tracker.gpsID ?? "—"}</td>
                    <td style={cell}>
                      {name}
                      {tracker.gpsAlias ? ` (${tracker.gpsAlias})` : ""}
                    </td>
                    <td style={cell}>{tracker.deviceName ?? "—"}</td>
                    <td style={cell}>{DEVICE_STATUS_LABELS[trackerStatus(tracker, now)]}</td>
                    <td style={cell}>{tracker.lastUpdatedAt?.toLocaleString() ?? "—"}</td>
                    <td style={cell}>{sample ? sample.latitude.toFixed(5) : "—"}</td>
                    <td style={cell}>{sample ? sample.longitude.toFixed(5) : "—"}</td>
                    <td style={cell}>{sample ? sample.speedUS_mph.toFixed(1) : "—"}</td>
                    <td style={cell}>{sample ? Math.round(sample.altitudeUS_feet) : "—"}</td>
                    <td style={cell}>{sample?.battery !== null && sample?.battery !== undefined ? `${sample.battery}%` : "—"}</td>
                    <td style={cell}>{sample?.lte_conditions || "—"}</td>
                    <td style={cell}>{tracker.selectedCourseKML?.name ?? "—"}</td>
                  </tr>
                );
              })}
              {trackers.data.length === 0 && (
                <tr>
                  <td style={cell} colSpan={12}>
                    No tracker documents yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </SBCard>
      )}
    </div>
  );
}

const cell: React.CSSProperties = {
  padding: "9px 12px",
  borderBottom: "1px solid var(--border-subtle)",
  whiteSpace: "nowrap",
};
